import {formatDate, formatNumber} from '@angular/common';
import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import * as echarts from 'echarts';
import {adbAlpha, adbChartTheme, adbPartialItem, adbRedrawOnThemeChange, adbTodaySoFar, adbTooltip} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {AdminDashboardPoint} from '@features/admin-panel/models/admin-dashboard.models';
import {KpiTone} from '../kpi-tile/admin-dashboard-kpi-tile.format';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const sparklineCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/**
 * A KPI's daily trend (OCA-E-159 charts.ts sparkOption): a straight 1.5 px line with a 10% fill in the tone colour
 * (good green, bad red, neutral blue), no axes, the y axis fitted to the values (a flat series through the middle),
 * edge to edge in a 40 px box, and a tooltip with the day, the value in the KPI's unit and what it counts.
 * ITP-E-211-02: a last point marked `partial` (today so far) is a faded dot at the end of a dashed segment, and its
 * tooltip says "today so far (HH:mm UTC)".
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-sparkline',
  templateUrl: './admin-dashboard-sparkline.component.html',
  styleUrls: ['./admin-dashboard-sparkline.component.scss']
})
export class AdminDashboardSparklineComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() series: AdminDashboardPoint[] = [];
  /** good → green, bad → red, neutral → blue (OCA-E-159 toneInk). */
  @Input() tone: KpiTone = 'neutral';
  /** A point's value as the tooltip shows it ("$12,500", "25%"); plain numbers by default. */
  @Input() format: (value: number) => string = value => formatNumber(value, 'en-US');
  /** What the value counts, after it in the tooltip ("865 active companies"). */
  @Input() name = '';
  /**
   * When a partial last point was counted (UTC ISO), for its tooltip. The KPI contract has no server time, so callers
   * leave it null and the sparkline uses the client clock at the moment it draws the series (ITP-E-211-02).
   */
  @Input() asOf: string | null = null;

  @ViewChild('chart', {static: true}) chartContainer: ElementRef<HTMLDivElement>;

  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;

  ngAfterViewInit(): void {
    this.chart = sparklineCharts.init(this.chartContainer.nativeElement);
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.render();
  }

  ngOnChanges(): void {
    this.render();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
    this.chart = null;
  }

  private render(): void {
    if (!this.chart) {
      return;
    }
    const points = this.series ?? [];
    const values = points.map(point => point.value);
    const labels = points.map(point => point.date ? formatDate(point.date, 'd MMM y', 'en-US', 'UTC') : '');
    const theme = adbChartTheme(this.chartContainer.nativeElement);
    const ink = this.tone === 'good' ? theme.green : this.tone === 'bad' ? theme.red : theme.blue;
    // A series that did not move is drawn through the middle (the honest reading of "flat"), not along the bottom.
    const flat = values.length > 0 && Math.min(...values) === Math.max(...values);
    // ITP-E-211-02. Today's live point (partial) is drawn apart: the solid line stops at the day before, and a dashed
    // segment leads from it to a faded dot. Its time is the caller's asOf, else the client clock now.
    const last = values.length - 1;
    const partial = last >= 0 && points[last]?.partial === true;
    const asOf = this.asOf ?? new Date().toISOString();
    const series: Record<string, unknown>[] = [{
      type: 'line',
      data: partial ? values.map((value, i) => i === last ? null : value) : values,
      showSymbol: false,
      symbolSize: 6,
      lineStyle: {width: 1.5, color: ink},
      itemStyle: {color: ink},
      areaStyle: {color: adbAlpha(ink, 0.1)}
    }];
    if (partial) {
      series.push({
        name: 'today so far',
        type: 'line',
        data: values.map((value, i) => i === last
          ? {value, symbol: 'circle', symbolSize: 6, itemStyle: adbPartialItem(theme, ink)}
          : i === last - 1 ? {value, symbol: 'none'} : null),
        showSymbol: true,
        showAllSymbol: true,
        lineStyle: {width: 1.5, color: ink, type: 'dashed'},
        itemStyle: {color: ink}
      });
    }
    const option = {
      animation: false,
      grid: {left: 0, right: 0, top: 6, bottom: 0},
      xAxis: {type: 'category', show: false, boundaryGap: false, data: labels},
      yAxis: flat
        ? {type: 'value', show: false, min: values[0] - 1, max: values[0] + 1}
        : {type: 'value', show: false, scale: true},
      series,
      // Appended to the body: the KPI tile clips its content to its rounded corners (overflow: hidden).
      tooltip: adbTooltip(theme, {
        appendTo: 'body',
        axisPointer: {type: 'line', lineStyle: {color: theme.ink2, width: 1}},
        formatter: (params: unknown): string => {
          const [first] = (Array.isArray(params) ? params : [params]) as {dataIndex?: number}[];
          const index = first?.dataIndex ?? 0;
          const value = values[index];
          const shown = typeof value === 'number' ? this.format(value) : '—';
          const day = labels[index] ?? '';
          const when = partial && index === last ? `${day}, ${adbTodaySoFar(asOf)}` : day;
          return `${when}<br/><b>${shown}</b>${this.name ? ' ' + this.name : ''}`;
        }
      })
    };
    this.chart.setOption(option, true);
  }
}
